import { set, keys, transform, some } from 'lodash';
import voca from 'voca';
import { camelizeSnakeParams, getLogger } from '@cld/upload-widget-common';
import { getIsStructuredMetadataSupported } from 'common/metadataDeps';
import { isFile } from 'utils/fileUtils';
import { getFileEncryptor } from 'utils/encryption';
import consts from './common/consts';
import { selectLastQueueMeta, selectSuccessCounter, selectUploadOptions, selectConsoleInfo } from './store/selectors';
import { types as ats } from './store/actions';
import { getTimestamp, getUploadSource, getCoordinatesString } from './common/utils';
import { getQueueConfigForId, getMetadataForFile, getQueueMetadataFromFileId } from './fileUpload/uploadFileMethods';

const logger = getLogger();

const PUBLIC_ID_RGX = /^([^?&#\\%<>]|%[0-9a-fA-F]{2})*$/i;

const getQueueHasConflictFiles = (fileId, options) => {
  const state = options.getState();
  const metadata = getQueueMetadataFromFileId(fileId, state);

  return !!metadata?.conflicts?.length;
};

const MULTI_PREP_REQUESTS_FIELDS = ['publicId', 'customCoordinates', 'folder', 'headers', 'filenameOverride'];

/**
 * in case more than one file uploaded and it has one of the above properties set
 * then we need to prepare/sign each file separately
 */
export const shouldUseMultiPrepRequests = (placeholderFiles, options) => {
  let result = false;

  if (placeholderFiles.length > 1) {
    const queueHasConflicts = getQueueHasConflictFiles(placeholderFiles[0].id, options);
    let diffsByAttribute;

    if (!queueHasConflicts) {
      diffsByAttribute = transform(
        placeholderFiles,
        (result, phf) => MULTI_PREP_REQUESTS_FIELDS.forEach((field) => phf[field] && set(result, `${field}["${phf[field]}"]`, phf[field])),
        {}
      );
    }

    result = queueHasConflicts || !!Object.keys(diffsByAttribute).find((attr) => keys(diffsByAttribute[attr]).length > 1);
  }

  return result;
};

const addUniqueValuesToPrepRequest = (request, phf, options) => ({
  ...request,
  ...(typeof request['asset_folder'] === 'string' ? { asset_folder: phf.folder || request.asset_folder } : { folder: phf.folder || request.folder }),
  public_id: phf.publicId || undefined, // using snake case for the client
  custom_coordinates: phf.customCoordinates // using snake case for the client
    ? getCoordinatesString(phf.customCoordinates)
    : undefined,
  metadata: getMetadataForFile(phf.id, options),
  headers: phf.headers,
  filename_override: phf.filenameOverride,
});

const getBasePrepareRequestData = (fileId, options) => {
  const uploadOptions = options.getUploadOptions();
  const config = getQueueConfigForId(fileId, options) || {};
  const tags = uploadOptions.tags?.length ? uploadOptions.tags.join(',') : undefined;
  const timestamp = config.uploadSignatureTimestamp || getTimestamp();

  return {
    timestamp,
    tags,
    context: config.context || undefined,
    // dont send empty folder
    folder: config.folder || undefined,
    asset_folder: config.assetFolder ?? undefined,
    use_filename: config.useFilename,
    unique_filename: config.uniqueFilename,
    invalidate: config.invalidate,
    // dont send empty preset
    upload_preset: uploadOptions.uploadPreset || config?.uploadPreset || undefined,
    source: getUploadSource(selectConsoleInfo(options.getState())),
    quality_analysis: config.qualityAnalysis,
    public_id_prefix: config.publicIdPrefix ?? undefined,
    use_asset_folder_as_public_id_prefix: config.useAssetFolderAsPublicIdPrefix,
    detection: config.detection,
    on_success: config.onSuccess,
    system_file: config.systemFile,
  };
};

const getUploadRequestData = (placeholderFiles, options) => {
  const baseRequest = getBasePrepareRequestData(placeholderFiles[0].id, options);
  let request = null;

  if (shouldUseMultiPrepRequests(placeholderFiles, options)) {
    // send array of params objects for signing
    request = placeholderFiles.map((phf) => addUniqueValuesToPrepRequest(baseRequest, phf, options));
  } else {
    // keeping single object instead of always sending array for backwards compatibility
    request = addUniqueValuesToPrepRequest(baseRequest, placeholderFiles[0], options);
  }

  return request;
};

const makePreBatchCall = (placeholderFiles, options) =>
  new Promise((resolve, reject) => {
    logger.log('[uploader.batchHandler]: pre-batch handler is configured - will delay loading of batch');
    const uploadOptions = options.getUploadOptions();
    const config = getQueueConfigForId(placeholderFiles[0].id, options) || {};
    const uploadPreset = (uploadOptions && uploadOptions.uploadPreset) || (config && config.uploadPreset) || undefined; // dont send empty preset

    options.externalApi.sendPreBatch(
      (data) => {
        // send off the external call for the pre-batch handler
        data = data || {};

        if (data.cancel === true) {
          reject({ cancel: true });
        } else {
          resolve(data);
        }
      },
      {
        widgetId: options.getWidgetId(),
        request: { files: placeholderFiles, options: { uploadPreset } },
      }
    );
  });

const handlePrepareResponse = (placeholderFiles, data, options) => {
  if (data) {
    const flowData = data; // flow is stupid
    logger.log('[uploader.batchHandler]: handling response for prepare request: ', flowData);

    // data array returned from prepare call must be in the same order as the placeholderfiles array
    const filesPayload = placeholderFiles.reduce((res, phf, idx) => {
      idx = flowData.data.length > 1 ? idx : 0;

      res[phf.id] = {
        timestamp: flowData.timestamp,
        ...camelizeSnakeParams(flowData.data[idx]),
      };

      return res;
    }, {});

    options.actions[ats.SET_UPLOADER_BATCH_PREPARED]({ files: filesPayload }); // store prepared params with the files in the store
  }
};

const makePrepareCall = (placeholderFiles, options) =>
  new Promise((resolve, reject) => {
    const request = getUploadRequestData(placeholderFiles, options);
    // when doing simple (backward compatible) signature (not prepare) timestamp isnt returned by the client
    const timestamp = Array.isArray(request) ? request[0].timestamp : request.timestamp;

    options.externalApi.requestPrepareParams(
      (data) => {
        const isCanceled = some(data, { cancel: true });

        if (isCanceled) {
          reject();
        } else {
          resolve({
            timestamp,
            data,
          });
        }
      },
      {
        widgetId: options.getWidgetId(),
        request,
        files: placeholderFiles,
      }
    );
  });

const cancelBatchUpload = (files, options) => {
  options.actions[ats.CANCEL_UPLOADER_FILES]({ files });
  options.notifyBatchCancelled();
};

// exported for testing purposes
export const handlePreRequests = (placeholderFiles, preBatchRequired, prepareRequired, options) => {
  const preReqPromises = [
    preBatchRequired ? makePreBatchCall(placeholderFiles, options) : null,
    prepareRequired ? makePrepareCall(placeholderFiles, options) : null,
  ];

  return Promise.all(preReqPromises)
    .then((results) => {
      handlePrepareResponse(placeholderFiles, results[1], options);
      // remove the queue from pending status - so its files can be (re)added to the upload
      setQueueAsPending(placeholderFiles, false, options);
    })
    .catch((reason) => {
      logger.log('[uploader.batchHandler]: pre-requests failed or cancelled - canceling batch!', reason);
      cancelBatchUpload(placeholderFiles, options);
    });
};

export const isValidPublicId = (id) => voca.isBlank(id) || PUBLIC_ID_RGX.test(id);

const BATCH_VALIDATORS = {
  [consts.BATCH_CANCEL_REASONS.MAX_EXCEEDED]: (data, config, options) =>
    config.maxFiles ? data.files.length <= config.maxFiles - selectSuccessCounter(options.getState()) : true,

  [consts.BATCH_CANCEL_REASONS.INVALID_PUBLIC_ID]: (data, config, options) => {
    let isValid = true;

    if (config.showAdvancedOptions) {
      const { publicId } = selectUploadOptions(options.getState());
      isValid = isValidPublicId(publicId);
    }

    return isValid;
  },
};

const validateBatch = (data, config, options) => {
  const reason = Object.keys(BATCH_VALIDATORS).find((key) => !BATCH_VALIDATORS[key](data, config, options));

  return {
    isValid: !reason,
    reason,
  };
};

const encryptFiles = (files, encryption) => {
  const encrypt = getFileEncryptor();

  return Promise.all(files.map((f) => (isFile(f) ? encrypt(f, encryption.key, encryption.iv) : f)));
};

const setQueueAsPending = (files, pending, options) => {
  options.actions[ats.SET_UPLOADER_QUEUE_PRECALLS_PENDING]({
    files, // pass files so reducer can locate the relevant queue to update
    pending,
  });
};

const isPendingMetadataSchema = (data, config) => !!(config.useMetadataCallback && !data.metadata) && getIsStructuredMetadataSupported();

/**
 * In case crop is enabled & max width/height configured (resize) & need to sign (prepare) params
 * or in case need to wait for structured metadata form values
 * then we need to delay the batchHandler's pre-processing work (pre-batch and/or prepare) until after the
 * image is resized
 */
const getShouldDelayPrepareAfterProcessing = (data, config) => {
  const coordinates = data.cropInfo && data.cropInfo[0];
  const hasMaxWidthHeightValidation = !config.validateMaxWidthHeight && (config.maxImageWidth || config.maxImageHeight);

  let delay = false;

  // this is needed only when upload signature is required
  if (config.requirePrepareParams && coordinates && hasMaxWidthHeightValidation) {
    logger.log('[uploader.batchHandler]: found max width/height validation with cropping. will delay pre-processing');
    delay = true;
  }

  return delay;
};

export default (options) => {
  const handleBatchAdded = async (data, existingBatch = false) => {
    logger.log(`[uploader.batchHandler]: ${existingBatch ? 'existing' : 'new'} upload batch was added - `, data);

    const config = options.getLatestConfig();

    existingBatch = existingBatch || !!data.metadata;

    if (!existingBatch && config.encryption) {
      // first of all encrypt the files if needed
      data.files = await encryptFiles(data.files, config.encryption);
    }

    let delayed = false;
    let cancelled = false;
    let cancelReason = null;

    const validateResult = existingBatch || validateBatch(data, config, options),
      isValidBatch = validateResult === true || validateResult.isValid;

    if (isValidBatch) {
      //select before calling addNewBatchData on the q-manager since it will change the store's state and can cause creation of a new queue
      const lastQueueIdInStore = selectLastQueueMeta(options.getState()).id;
      const preBatchRequired = config.usePreBatchCallback;
      const prepareRequired = config.requirePrepareParams;
      // need to delay upload until pre-calls responses are returned from client
      const preCallsNeeded = preBatchRequired || prepareRequired;
      const isPendingMetadata = isPendingMetadataSchema(data, config);
      // cant call pre-batch or prepare handling YET since cropping coordinates may change due to resize/orientation
      const canRunPreCalls = existingBatch || !getShouldDelayPrepareAfterProcessing(data, config);

      // delay the uploads in case need to wait for pre-batch, prepare, or metadata callbacks
      delayed = (preCallsNeeded && canRunPreCalls) || isPendingMetadata;

      const placeholderFiles = existingBatch
        ? // this is already a batch created by queueManager - we can use files as is
          data.files
        : // this is a new batch and we need to convert to UW data from the info received from outside (jquery file upload or 3rd party)
          options.queuesManager.addNewBatchData(data, {
            delayed,
            // need to store as flag so wont submit upload until pre-calls are done
            delayedPreCalls: preCallsNeeded && !canRunPreCalls,
            multiple: config.multiple,
          });

      if (!existingBatch) {
        options.actions[ats.CLEAN_POST_BATCH_PROP_LIST]();
      }

      if (data.metadata) {
        options.actions[ats.SET_UPLOADER_QUEUE_METADATA](data);
      }

      if (canRunPreCalls && !isPendingMetadata) {
        if (existingBatch) {
          logger.log('[uploader.batchHandler]: setting queue as pending for existing batch');
          // need to set the queue as pending pre calls for existing batch
          setQueueAsPending(placeholderFiles, true, options);
        }

        handlePreRequests(placeholderFiles, preBatchRequired, prepareRequired, options) // handles pre flows (if configured) asynchronously
          .catch((reason) => {
            logger.log('[uploader.batchHandler]: pre flows handling failed with: ', reason);
          });
      } else {
        logger.log('[uploader.batchHandler]: not running prebatch/prepare- may change params due to image processing or need for metadata schema');
      }

      data.files = placeholderFiles; // change the file objects the jquery fileupload works with!!!

      if (!delayed) {
        // doing after addNewBatchData because at this point last id could have changed
        const lastLoadedQueueId = options.getLoadedQueueId();
        // compare the currently loaded queue with the last queue - if they're not the same then we need to delay
        delayed = !lastLoadedQueueId || lastLoadedQueueId !== lastQueueIdInStore;
      }
    } else {
      cancelled = true;
      cancelReason = validateResult.reason;
    }

    return {
      // result = false will ensure files arent added to the fileupload queue
      result: !(delayed || cancelled),
      cancelled,
      reason: cancelReason,
    };
  };

  const handleMetadataSchemaRetrieval = (data) =>
    new Promise((resolve) => {
      options.externalApi.requestMetadataSchema(
        (clientData) => {
          if (clientData?.cancel === true) {
            logger.log('[uploader]: md schema callback returned cancel = true - cancelling batch');
            cancelBatchUpload(data.files, options);
          }

          resolve(clientData);
        },
        {
          widgetId: options.getWidgetId(),
          files: data.files,
          data: getUploadRequestData(data.files, options),
        }
      );
    });

  return {
    handleBatchAdded,
    handleMetadataSchemaRetrieval,
  };
};
